<template>
  <main>
    <div class="container super-narrow">
      <div class="register-panel">


        <h3 class="center h2 text-center mb-4 font-weight-normal">Create an Account</h3>
        <p class="text-center mx-auto">
          After creating your account, you will use those same credentials to log into the game.
        </p>

        <p v-if="awaitingConfirmation" class="success-message m-auto mb-4">
          <i class="fas fa-check-circle mr-1"></i>
          We sent you a confirmation email. Please click the link to complete registration!
        </p>

        <div v-if="errors.details" class="error-message mb-4">
          <p>{{ errors.details }}</p>
        </div>

        <form @submit.prevent="executeCaptcha">
          <div class="mb-3">
            <label for="registerUsername" class="form-label sr-only">Username</label>
            <input type="text" v-model="username" class="form-control mb-0 p-3" placeholder="Username" id="registerUsername" name="xalor_username" autocomplete="username" required>
            <template v-if="errors.username">
              <small v-for="error in errors.username" :key="error" class="text-danger">{{ error }}</small>
            </template>
          </div>

          <div class="mb-3">
            <label for="registerEmail" class="form-label sr-only">Email Address</label>
            <input type="email" v-model="email" class="form-control mb-0 p-3" placeholder="Email Address" id="registerEmail" name="xalor_email" autocomplete="email" required>
            <template v-if="errors.email">
              <small v-for="error in errors.email" :key="error" class="text-danger">{{ error }}</small>
            </template>
          </div>

          <div class="mb-3 password-container">
            <label for="registerPassword" class="form-label sr-only">Password</label>
            <input
                type="password"
                v-model="password"
                class="form-control mb-0 p-3"
                placeholder="Password"
                id="registerPassword"
                @focus="showPasswordHelp = true"
                @blur="showPasswordHelp = false"
                required
            >

            <div
                v-if="showPasswordHelp && !passwordIsValid"
                class="password-tooltip"
            >
              <p>Your password must:</p>
              <ul>
                <li :class="passwordLengthValid ? 'valid' : ''">Be at least 8 characters long</li>
                <li :class="passwordHasLetter ? 'valid' : ''">Contain at least one letter</li>
                <li :class="passwordHasNumber ? 'valid' : ''">Contain at least one number</li>
                <li :class="passwordHasSpecial ? 'valid' : ''">Contain at least one special character</li>
              </ul>
            </div>

            <template v-if="errors.password">
              <small v-for="error in errors.password" :key="error" class="text-danger">{{ error }}</small>
            </template>
          </div>

          <div class="mb-3 position-relative">
            <label for="registerConfirmPassword" class="form-label sr-only">Confirm Password</label>
            <input
                type="password"
                v-model="passwordConfirmation"
                class="form-control mb-0 p-3"
                placeholder="Confirm Password"
                id="registerConfirmPassword"
                @focus="showConfirmHelp = true"
                @blur="showConfirmHelp = false"
                required
            >

            <!-- Popup box -->
            <div
                v-if="showConfirmHelp && passwordConfirmation && !passwordsMatch"
                class="password-popup"
            >
              Passwords do not match
            </div>

            <!-- Server errors -->
            <template v-if="errors.password_confirmation">
              <small v-for="error in errors.password_confirmation" :key="error" class="text-danger">{{ error }}</small>
            </template>
          </div>

          <div class="mb-3 form-check">
            <input class="form-check-input mr-2" type="checkbox" id="registerTOS" required>
              I accept the
              <router-link class="text-white" to="/terms">Terms & Conditions</router-link>
              .
          </div>

          <VueHcaptcha ref="hcaptcha" sitekey="332bd50d-ceed-4901-b253-345b9792af15" size="invisible" @verify="register"
                       class="text-center"/>

          <button type="submit" class="btn full-width btn-gold" :disabled="!passwordIsValid || !passwordsMatch">Register</button>
        </form>
        <p></p>
        <p class="text-center mb-0">
          Already signed up?
          <router-link to="/login">Log in here</router-link>
        </p>
      </div>
    </div>
  </main>
</template>


<script>
import APIClient from "@/services/APIClient.js"
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha'

export default {
  components: {
    VueHcaptcha
  },
  data() {
    return {
      client: null,
      loading: false,
      awaitingConfirmation: false,
      username: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      errors: [],
      showConfirmHelp: false,
    }
  },
  computed: {
    passwordLengthValid() {
      return this.password.length >= 8
    },
    passwordHasLetter() {
      return /[a-zA-Z]/.test(this.password)
    },
    passwordHasNumber() {
      return /\d/.test(this.password)
    },
    passwordHasSpecial() {
      return /[^a-zA-Z0-9]/.test(this.password)
    },
    passwordIsValid() {
      return (
          this.passwordLengthValid &&
          this.passwordHasLetter &&
          this.passwordHasNumber &&
          this.passwordHasSpecial
      )
    },
    passwordsMatch() {
      return this.password && this.password === this.passwordConfirmation
    }
  },
  created() {
    document.title = 'Register - Xalor'

    this.client = new APIClient();
  },
  methods: {
    executeCaptcha() {
      this.loading = true
      this.awaitingConfirmation = false
      this.$refs.hcaptcha.execute();
    },
    register(tokenStr) {
      console.log('catpcha token:', tokenStr)
      this.loading = true
      this.awaitingConfirmation = false
      this.errors = []

      const payload = {
        username: this.username,
        email: this.email,
        password: this.password,
        password_confirmation: this.passwordConfirmation,
        captchaToken: tokenStr
      }

      this.client.postUser(payload).then((response) => {
        console.log(response)
        if (response.status === 200) {
          this.awaitingConfirmation = true
        } else if (response.status === 201) {
          this.$router.push('/welcome')
        }
        this.loading = false
      }).catch((err) => {
        console.log(err)
        if (err.response.status === 429)
          this.errors = {details: "You have been rate limited, please try again later."}
        else
          this.errors = err.response.data
        this.loading = false
        this.awaitingConfirmation = false
      })
    },
  }
}
</script>
